import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { format, parseISO, isPast, subDays } from 'date-fns'
import { es, enUS } from 'date-fns/locale'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Textarea } from '../components/ui/textarea'
import { Badge } from '../components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { BottomSheet } from '../components/BottomSheet'
import { ShareTaskModal } from '../components/ShareTaskModal'
import { TaskEvidencesLoader } from '../components/TaskEvidences'
import { TaskDetailModal } from '../components/TaskDetailModal'
import { OfflineBanner, TaskSyncBadge } from '../components/OfflineBanner'
import { toast } from 'sonner'
import { useLanguage } from '../context/LanguageContext'
import { useOffline, SYNC_STATUS } from '../context/OfflineContext'
import { 
  Plus, Clock, CheckCircle2, AlertTriangle, Play, 
  Trash2, Edit, Calendar, RefreshCw, Bell, BellOff,
  Flame, Minus, ArrowDown, Ban, Users, MessageSquare, User,
  WifiOff, Eye, Search, X
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

export default function TareasPage() {
  const { t, locale } = useLanguage()
  const { 
    isOnline, 
    isDBReady, 
    isSyncing,
    cacheTasks, 
    getCachedTasks,
    createTaskOffline,
    updateTaskOffline,
    syncAll,
    forceSync
  } = useOffline()
  
  const [tasks, setTasks] = useState([])
  const [sharedTasks, setSharedTasks] = useState([])
  const [pendingInvites, setPendingInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingTask, setEditingTask] = useState(null)
  const [newTaskId, setNewTaskId] = useState(null) // Track ID of newly created task for evidences
  const [activeTab, setActiveTab] = useState('all')
  const [notificationsEnabled, setNotificationsEnabled] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [selectedTaskForShare, setSelectedTaskForShare] = useState(null)
  const [selectedTaskForDetail, setSelectedTaskForDetail] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  
  const dateLocale = locale === 'es-MX' ? es : enUS
  
  // Debounce search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])
  
  // Normalize text for search (remove accents and convert to lowercase)
  const normalizeText = (text) => {
    if (!text) return ''
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
  }

  const STATUS_CONFIG = {
    'Pendiente': { color: 'bg-blue-500', icon: Clock, label: t('tasks.pending') },
    'En progreso': { color: 'bg-amber-500', icon: Play, label: t('tasks.inProgress') },
    'Bloqueado': { color: 'bg-red-600', icon: Ban, label: t('tasks.blocked') },
    'Terminado': { color: 'bg-emerald-500', icon: CheckCircle2, label: t('tasks.completed') },
    'Vencida': { color: 'bg-rose-500', icon: AlertTriangle, label: t('tasks.overdue') }
  }

  const URGENCY_CONFIG = {
    'URGENTE': { color: 'bg-red-500', borderColor: 'border-l-red-500', textColor: 'text-red-500', icon: Flame, label: t('tasks.urgent') },
    'MEDIO': { color: 'bg-amber-500', borderColor: 'border-l-amber-500', textColor: 'text-amber-500', icon: Minus, label: t('tasks.medium') },
    'BAJO': { color: 'bg-sky-500', borderColor: 'border-l-sky-500', textColor: 'text-sky-500', icon: ArrowDown, label: t('tasks.low') }
  }
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    due_time: '',
    urgencia: 'MEDIO'
  })

  const fetchTasks = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true)
    
    // If offline, load from cache
    if (!isOnline && isDBReady) {
      try {
        const cachedTasks = await getCachedTasks()
        setTasks(cachedTasks)
        setSharedTasks([]) // Shared tasks not available offline
        setPendingInvites([]) // Invites not available offline
        console.log('[Tasks] Loaded from cache:', cachedTasks.length, 'tasks')
      } catch (error) {
        console.error('[Tasks] Error loading cached tasks:', error)
      } finally {
        setLoading(false)
        setRefreshing(false)
      }
      return
    }
    
    // Online: fetch from server
    try {
      const [tasksRes, sharedRes, pendingRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/tasks/shared-with-me`),
        axios.get(`${API}/task-shares/pending`)
      ])
      
      const serverTasks = tasksRes.data
      setTasks(serverTasks)
      setSharedTasks(sharedRes.data)
      setPendingInvites(pendingRes.data)
      
      // Cache tasks for offline use
      // Cache: all active (non-completed) + last 30 days completed
      if (isDBReady) {
        const thirtyDaysAgo = subDays(new Date(), 30).toISOString()
        const tasksToCache = serverTasks.filter(task => {
          if (task.status !== 'Terminado') return true // All active tasks
          // For completed: only last 30 days
          return task.finished_at && task.finished_at >= thirtyDaysAgo
        })
        await cacheTasks(tasksToCache)
        console.log('[Tasks] Cached', tasksToCache.length, 'tasks for offline')
      }
    } catch (error) {
      console.error('Error fetching tasks:', error)
      
      // Fallback to cache on network error
      if (isDBReady) {
        try {
          const cachedTasks = await getCachedTasks()
          if (cachedTasks.length > 0) {
            setTasks(cachedTasks)
            toast.info(locale === 'es-MX' 
              ? 'Mostrando datos en caché' 
              : 'Showing cached data'
            )
          }
        } catch (cacheError) {
          console.error('[Tasks] Cache fallback failed:', cacheError)
        }
      }
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [isOnline, isDBReady, getCachedTasks, cacheTasks, locale])

  // Manual refresh handler
  const handleRefresh = () => {
    if (isOnline) {
      // First sync any pending changes, then fetch
      syncAll().then(() => fetchTasks(true))
    } else {
      fetchTasks(true)
    }
  }

  useEffect(() => {
    fetchTasks()
    checkNotificationPermission()
    // Check for existing push subscription on mount
    checkExistingSubscription()
    
    // Auto-refresh when user comes back to the page
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchTasks(false) // Silent refresh
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [fetchTasks])
  
  // Re-fetch when coming back online
  useEffect(() => {
    if (isOnline && isDBReady) {
      fetchTasks(false)
    }
  }, [isOnline, isDBReady, fetchTasks])

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted')
    }
  }

  const checkExistingSubscription = async () => {
    try {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.getSubscription()
        
        if (subscription && Notification.permission === 'granted') {
          // Re-register subscription with backend (in case it was lost)
          await axios.post(`${API}/push/subscribe`, {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
              auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
            }
          })
          console.log('[PUSH] Existing subscription verified with backend')
        }
      }
    } catch (error) {
      console.error('[PUSH] Error checking existing subscription:', error)
    }
  }

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error(t('tasks.noSupport'))
      return
    }

    if (!('serviceWorker' in navigator)) {
      toast.error(t('tasks.noSupport'))
      return
    }

    try {
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        setNotificationsEnabled(true)
        await subscribeToPush()
        toast.success(t('tasks.notificationsEnabled'))
      } else {
        toast.error(locale === 'es-MX' ? 'Permiso de notificaciones denegado' : 'Notification permission denied')
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error)
      toast.error(t('common.error'))
    }
  }

  const subscribeToPush = async () => {
    try {
      // Wait for service worker to be ready
      const registration = await navigator.serviceWorker.ready
      console.log('[PUSH] Service worker ready')
      
      // Get VAPID public key from server
      const vapidResponse = await axios.get(`${API}/push/vapid-key`)
      const vapidPublicKey = vapidResponse.data.publicKey
      
      if (!vapidPublicKey) {
        throw new Error('VAPID public key not configured on server')
      }
      
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4)
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
        const rawData = window.atob(base64)
        const outputArray = new Uint8Array(rawData.length)
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i)
        }
        return outputArray
      }
      
      // Check if already subscribed
      let subscription = await registration.pushManager.getSubscription()
      
      if (!subscription) {
        // Create new subscription
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        })
        console.log('[PUSH] New push subscription created')
      } else {
        console.log('[PUSH] Using existing push subscription')
      }
      
      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
        }
      })
      
      console.log('[PUSH] Subscription registered with backend successfully')
      
      // Send a test notification to verify
      try {
        await axios.post(`${API}/push/test`)
        console.log('[PUSH] Test notification sent')
      } catch (testError) {
        console.warn('[PUSH] Test notification failed:', testError)
      }
      
    } catch (error) {
      console.error('[PUSH] Error subscribing to push:', error)
      toast.error('Error al configurar notificaciones: ' + (error.message || 'Error desconocido'))
    }
  }

  const sendTestNotification = async () => {
    try {
      await axios.post(`${API}/push/test`)
      toast.success('Notificación de prueba enviada')
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar prueba')
    }
  }

  const openNewTask = () => {
    setEditingTask(null)
    setNewTaskId(null) // Reset new task ID
    const now = new Date()
    const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000)
    setFormData({
      title: '',
      description: '',
      due_date: format(tomorrow, 'yyyy-MM-dd'),
      due_time: '12:00',
      urgencia: 'MEDIO'
    })
    setShowModal(true)
  }

  const openEditTask = (task) => {
    setEditingTask(task)
    setNewTaskId(null)
    const dueDate = parseISO(task.due_date)
    setFormData({
      title: task.title,
      description: task.description || '',
      due_date: format(dueDate, 'yyyy-MM-dd'),
      due_time: format(dueDate, 'HH:mm'),
      urgencia: task.urgencia || 'MEDIO'
    })
    setShowModal(true)
  }

  // Create task silently for evidences (called when adding evidence to new task)
  // Returns: { success: true, id: string } | { success: false, reason: string }
  const createTaskForEvidences = async () => {
    // Validate minimum required data
    if (!formData.title.trim()) {
      toast.error(locale === 'es-MX' ? 'Ingresa un título primero' : 'Enter a title first')
      return { success: false, reason: 'no_title' }
    }
    
    if (!formData.due_date || !formData.due_time) {
      toast.error(locale === 'es-MX' ? 'Selecciona fecha y hora primero' : 'Select date and time first')
      return { success: false, reason: 'no_date' }
    }
    
    const dueDateTime = new Date(`${formData.due_date}T${formData.due_time}:00`)
    
    const taskData = {
      title: formData.title,
      description: formData.description,
      due_date: dueDateTime.toISOString(),
      urgencia: formData.urgencia
    }
    
    try {
      // Create the task on the server
      const response = await axios.post(`${API}/tasks`, taskData)
      const createdTask = response.data
      
      // Update local state
      setNewTaskId(createdTask.id)
      setTasks(prev => [createdTask, ...prev])
      
      toast.success(locale === 'es-MX' ? 'Tarea creada. Ahora puedes agregar evidencias.' : 'Task created. Now you can add evidences.')
      
      return { success: true, id: createdTask.id }
    } catch (error) {
      console.error('Error creating task for evidences:', error)
      toast.error(locale === 'es-MX' ? 'Error al crear la tarea' : 'Error creating task')
      return { success: false, reason: 'server_error' }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.title.trim()) {
      toast.error(locale === 'es-MX' ? 'El título es obligatorio' : 'Title is required')
      return
    }
    
    if (!formData.due_date || !formData.due_time) {
      toast.error(locale === 'es-MX' ? 'La fecha y hora son obligatorias' : 'Date and time are required')
      return
    }
    
    const dueDateTime = new Date(`${formData.due_date}T${formData.due_time}:00`)
    
    const taskData = {
      title: formData.title,
      description: formData.description,
      due_date: dueDateTime.toISOString(),
      urgencia: formData.urgencia
    }
    
    // OFFLINE MODE: Save locally
    if (!isOnline && isDBReady) {
      try {
        if (editingTask || newTaskId) {
          // Update existing task (or task created for evidences)
          const taskIdToUpdate = editingTask?.id || newTaskId
          const updatedTask = await updateTaskOffline(taskIdToUpdate, taskData)
          if (updatedTask) {
            setTasks(prev => prev.map(t => t.id === taskIdToUpdate ? updatedTask : t))
            toast.success(t('tasks.savedLocally'))
          }
        } else {
          // Create new task
          const newTask = await createTaskOffline(taskData)
          if (newTask) {
            setTasks(prev => [newTask, ...prev])
            toast.success(t('tasks.savedLocally'))
          }
        }
        setShowModal(false)
        setNewTaskId(null)
      } catch (error) {
        console.error('[Tasks] Offline save error:', error)
        toast.error(t('tasks.errorSaving'))
      }
      return
    }
    
    // ONLINE MODE: Save to server
    try {
      if (editingTask) {
        // Update existing task being edited
        await axios.put(`${API}/tasks/${editingTask.id}`, taskData)
        toast.success(t('tasks.taskUpdated'))
      } else if (newTaskId) {
        // Update task that was created for evidences
        await axios.put(`${API}/tasks/${newTaskId}`, taskData)
        toast.success(t('tasks.taskUpdated'))
      } else {
        // Create new task
        await axios.post(`${API}/tasks`, taskData)
        toast.success(t('tasks.taskSaved'))
      }
      
      setShowModal(false)
      setNewTaskId(null)
      fetchTasks()
    } catch (error) {
      // On network error, try offline save as fallback
      if (isDBReady && error.message?.includes('Network')) {
        try {
          if (editingTask || newTaskId) {
            const taskIdToUpdate = editingTask?.id || newTaskId
            const updatedTask = await updateTaskOffline(taskIdToUpdate, taskData)
            if (updatedTask) {
              setTasks(prev => prev.map(t => t.id === taskIdToUpdate ? updatedTask : t))
              toast.info(locale === 'es-MX' 
                ? 'Guardado localmente. Se sincronizará cuando vuelvas a estar en línea.'
                : 'Saved locally. Will sync when back online.'
              )
            }
          } else {
            const newTask = await createTaskOffline(taskData)
            if (newTask) {
              setTasks(prev => [newTask, ...prev])
              toast.info(locale === 'es-MX' 
                ? 'Guardado localmente. Se sincronizará cuando vuelvas a estar en línea.'
                : 'Saved locally. Will sync when back online.'
              )
            }
          }
          setShowModal(false)
          setNewTaskId(null)
          return
        } catch (offlineError) {
          console.error('[Tasks] Offline fallback failed:', offlineError)
        }
      }
      toast.error(t('tasks.errorSaving'))
    }
  }

  const updateTaskStatus = async (taskId, newStatus) => {
    const updateData = { status: newStatus }
    
    // OFFLINE MODE
    if (!isOnline && isDBReady) {
      try {
        const updatedTask = await updateTaskOffline(taskId, updateData)
        if (updatedTask) {
          setTasks(prev => prev.map(t => t.id === taskId ? updatedTask : t))
          toast.success(newStatus === 'Terminado' 
            ? t('tasks.taskCompleted') 
            : t('tasks.savedLocally')
          )
          
          if (newStatus === 'Terminado') {
            window.dispatchEvent(new CustomEvent('bonsai-update'))
          }
        }
      } catch (error) {
        toast.error(t('tasks.errorSaving'))
      }
      return
    }
    
    // ONLINE MODE
    try {
      await axios.put(`${API}/tasks/${taskId}`, updateData)
      toast.success(newStatus === 'Terminado' ? t('tasks.taskCompleted') : t('tasks.taskUpdated'))
      
      // Trigger bonsai update when task is completed
      if (newStatus === 'Terminado') {
        window.dispatchEvent(new CustomEvent('bonsai-update'))
      }
      
      fetchTasks()
    } catch (error) {
      // Fallback to offline
      if (isDBReady) {
        try {
          const updatedTask = await updateTaskOffline(taskId, updateData)
          if (updatedTask) {
            setTasks(prev => prev.map(t => t.id === taskId ? updatedTask : t))
            toast.info(locale === 'es-MX' 
              ? 'Guardado localmente. Se sincronizará cuando vuelvas a estar en línea.'
              : 'Saved locally. Will sync when back online.'
            )
            return
          }
        } catch (offlineError) {
          console.error('[Tasks] Offline fallback failed:', offlineError)
        }
      }
      toast.error(t('tasks.errorSaving'))
    }
  }

  const rescheduleTask = async (task) => {
    openEditTask(task)
  }

  const deleteTask = async (taskId) => {
    if (!window.confirm(locale === 'es-MX' ? '¿Eliminar esta tarea?' : 'Delete this task?')) return
    
    try {
      await axios.delete(`${API}/tasks/${taskId}`)
      toast.success(t('tasks.taskDeleted'))
      fetchTasks()
    } catch (error) {
      toast.error(t('tasks.errorDeleting'))
    }
  }

  const openShareModal = (task) => {
    setSelectedTaskForShare(task)
    setShowShareModal(true)
  }

  const acceptInvite = async (invite) => {
    try {
      await axios.post(`${API}/task-shares/accept`, { invite_token: invite.invite_token })
      toast.success(locale === 'es-MX' ? 'Invitación aceptada' : 'Invitation accepted')
      fetchTasks()
    } catch (error) {
      toast.error(error.response?.data?.detail || (locale === 'es-MX' ? 'Error al aceptar' : 'Error accepting'))
    }
  }

  const updateSharedTaskStatus = async (taskId, shareId, newStatus, comment = '') => {
    try {
      await axios.put(`${API}/tasks/shared/${taskId}`, { 
        status: newStatus,
        comment: comment || undefined
      })
      toast.success(newStatus === 'Terminado' ? t('tasks.taskCompleted') : t('tasks.taskUpdated'))
      fetchTasks()
    } catch (error) {
      toast.error(error.response?.data?.detail || t('tasks.errorSaving'))
    }
  }

  const filteredTasks = tasks.filter(task => {
    // First, apply search filter
    if (debouncedSearch) {
      const normalizedQuery = normalizeText(debouncedSearch)
      const normalizedTitle = normalizeText(task.title)
      const normalizedDescription = normalizeText(task.description)
      
      const matchesSearch = normalizedTitle.includes(normalizedQuery) || 
                           normalizedDescription.includes(normalizedQuery)
      if (!matchesSearch) return false
    }
    
    // Then, apply tab filter
    if (activeTab === 'all') return true
    if (activeTab === 'pending') return task.status === 'Pendiente' || task.status === 'En progreso'
    if (activeTab === 'blocked') return task.status === 'Bloqueado'
    if (activeTab === 'completed') return task.status === 'Terminado'
    if (activeTab === 'overdue') return task.status === 'Vencida'
    if (activeTab === 'shared') return false // Shared tab shows sharedTasks separately
    return true
  })

  const taskCounts = {
    all: tasks.length,
    pending: tasks.filter(t => t.status === 'Pendiente' || t.status === 'En progreso').length,
    blocked: tasks.filter(t => t.status === 'Bloqueado').length,
    completed: tasks.filter(t => t.status === 'Terminado').length,
    overdue: tasks.filter(t => t.status === 'Vencida').length,
    shared: sharedTasks.length + pendingInvites.length
  }

  return (
    <div className="pb-32 max-w-5xl mx-auto">
      {/* Offline Banner */}
      <OfflineBanner />
      
      <div className="p-4 md:p-8">
      {/* Header - Mobile optimized */}
      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold mb-1 flex items-center gap-2">
            {t('tasks.title')}
            {!isOnline && (
              <WifiOff className="w-5 h-5 text-rose-500" data-testid="offline-indicator" />
            )}
          </h1>
          <p className="text-sm text-muted-foreground">{locale === 'es-MX' ? 'Gestiona tus pendientes' : 'Manage your to-do list'}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="icon"
            onClick={notificationsEnabled ? sendTestNotification : requestNotificationPermission}
            className={`rounded-full h-10 w-10 ${notificationsEnabled ? 'border-emerald-500/50' : ''}`}
            title={notificationsEnabled ? (locale === 'es-MX' ? 'Enviar notificación de prueba' : 'Send test notification') : t('tasks.enableNotifications')}
            data-testid="notifications-btn"
            disabled={!isOnline}
          >
            {notificationsEnabled ? (
              <Bell className="w-4 h-4 text-emerald-500" />
            ) : (
              <BellOff className="w-4 h-4 text-muted-foreground" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing || isSyncing}
            className="h-10 w-10 rounded-full"
            data-testid="refresh-tasks-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing || isSyncing ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            onClick={openNewTask}
            size="sm"
            className="rounded-full h-10 px-4 bg-blue-500 hover:bg-blue-600"
            data-testid="new-task-btn"
          >
            <Plus className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">{t('tasks.new')}</span>
          </Button>
        </div>
      </div>

      {/* Notification status banner */}
      {!notificationsEnabled && isOnline && (
        <Card className="mb-4 bg-amber-500/10 border-amber-500/30">
          <CardContent className="p-3 flex items-center gap-3">
            <BellOff className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{t('tasks.notificationsDisabled')}</p>
              <p className="text-xs text-muted-foreground">{locale === 'es-MX' ? 'No recibirás alertas cuando tus tareas venzan' : "You won't receive alerts when your tasks are due"}</p>
            </div>
            <Button size="sm" variant="outline" onClick={requestNotificationPermission} className="flex-shrink-0">
              {locale === 'es-MX' ? 'Activar' : 'Enable'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Search Bar */}
      <div className="mb-4 relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={locale === 'es-MX' ? 'Buscar tareas por título o descripción...' : 'Search tasks by title or description...'}
            className="pl-10 pr-10 h-11 rounded-full bg-secondary/50 border-secondary focus:bg-background transition-colors"
            data-testid="task-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted transition-colors"
              data-testid="task-search-clear"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>
        {debouncedSearch && (
          <p className="text-xs text-muted-foreground mt-2 px-1">
            {locale === 'es-MX' 
              ? `${filteredTasks.length} resultado${filteredTasks.length !== 1 ? 's' : ''} para "${debouncedSearch}"`
              : `${filteredTasks.length} result${filteredTasks.length !== 1 ? 's' : ''} for "${debouncedSearch}"`
            }
          </p>
        )}
      </div>

      {/* Tabs - Horizontal scroll on mobile */}
      <div className="mb-6 -mx-4 px-4 overflow-x-auto scrollbar-hide">
        <div className="flex gap-2 min-w-max pb-2">
          {[
            { key: 'all', label: t('tasks.allTasks'), count: taskCounts.all },
            { key: 'pending', label: t('tasks.activeTasks'), count: taskCounts.pending },
            { key: 'blocked', label: t('tasks.blocked'), count: taskCounts.blocked },
            { key: 'overdue', label: t('tasks.overdue'), count: taskCounts.overdue },
            { key: 'completed', label: t('tasks.completedTasks'), count: taskCounts.completed },
            { key: 'shared', label: locale === 'es-MX' ? 'Compartidas' : 'Shared', count: taskCounts.shared },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'bg-foreground text-background'
                  : 'bg-secondary text-foreground hover:bg-secondary/80'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* Task List */}
      <div className="space-y-3">
        {activeTab === 'shared' ? (
          /* Shared Tasks Section */
          <div className="space-y-6">
            {/* Pending Invitations */}
            {pendingInvites.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {locale === 'es-MX' ? 'Invitaciones pendientes' : 'Pending invitations'}
                </h3>
                <div className="space-y-2">
                  {pendingInvites.map((invite) => (
                    <Card key={invite.id} className="border-amber-500/30 bg-amber-500/5" data-testid={`invite-${invite.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium">{invite.task_title}</h4>
                            <p className="text-sm text-muted-foreground line-clamp-1">{invite.task_description}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                              <User className="w-3 h-3" />
                              <span>{locale === 'es-MX' ? 'De' : 'From'}: {invite.owner_name}</span>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => acceptInvite(invite)}
                            className="rounded-full"
                            data-testid={`accept-invite-${invite.id}`}
                          >
                            {locale === 'es-MX' ? 'Aceptar' : 'Accept'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Active Shared Tasks */}
            {sharedTasks.length > 0 ? (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {locale === 'es-MX' ? 'Tareas compartidas conmigo' : 'Tasks shared with me'}
                </h3>
                <div className="space-y-3">
                  {sharedTasks.map((task) => {
                    const StatusIcon = STATUS_CONFIG[task.status]?.icon || Clock
                    const statusColor = STATUS_CONFIG[task.status]?.color || 'bg-gray-500'
                    const dueDate = parseISO(task.due_date)
                    const urgencyConfig = URGENCY_CONFIG[task.urgencia] || URGENCY_CONFIG['MEDIO']
                    
                    return (
                      <Card 
                        key={task.id} 
                        className={`transition-all border-l-4 ${urgencyConfig.borderColor} cursor-pointer hover:bg-accent/50`}
                        data-testid={`shared-task-${task.id}`}
                        onClick={() => {
                          setSelectedTaskForDetail({ ...task, isShared: true })
                          setShowDetailModal(true)
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-full ${statusColor} flex items-center justify-center flex-shrink-0`}>
                              <StatusIcon className="w-5 h-5 text-white" />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className={`font-semibold text-base ${task.status === 'Terminado' ? 'line-through text-muted-foreground' : ''}`}>
                                  {task.title}
                                </h3>
                                <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
                                  <Users className="w-3 h-3 mr-1" />
                                  Compartida
                                </Badge>
                              </div>
                              
                              {task.description && (
                                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                                  {task.description}
                                </p>
                              )}
                              
                              <div className="flex flex-wrap items-center gap-2 mb-3">
                                <span className="text-xs flex items-center gap-1 text-muted-foreground">
                                  <User className="w-3 h-3" />
                                  {task.owner_name}
                                </span>
                                <span className="text-xs flex items-center gap-1 text-muted-foreground">
                                  <Calendar className="w-3 h-3" />
                                  {format(dueDate, "d MMM, HH:mm", { locale: dateLocale })}
                                </span>
                                <Badge variant="outline" className={`${statusColor} text-white text-xs px-2`}>
                                  {task.status}
                                </Badge>
                              </div>
                              
                              {/* Comments */}
                              {task.comments && task.comments.length > 0 && (
                                <div className="mb-3 p-2 bg-muted/30 rounded-md">
                                  <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                                    <MessageSquare className="w-3 h-3" />
                                    {locale === 'es-MX' ? 'Último comentario' : 'Last comment'}
                                  </p>
                                  <p className="text-sm">{task.comments[0].comment}</p>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    — {task.comments[0].user_name}
                                  </p>
                                </div>
                              )}
                              
                              {/* Actions for collaborator - ALWAYS allow status change */}
                              <div className="flex items-center gap-2">
                                {task.status !== 'Terminado' && (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => updateSharedTaskStatus(task.id, task.share_id, 'Terminado', '')}
                                    className="rounded-full text-xs h-8"
                                    data-testid={`complete-shared-btn-${task.id}`}
                                  >
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    {locale === 'es-MX' ? 'Completar' : 'Complete'}
                                  </Button>
                                )}
                                <Select
                                  value={task.status}
                                  onValueChange={(value) => updateSharedTaskStatus(task.id, task.share_id, value, '')}
                                >
                                  <SelectTrigger className="w-auto h-8 text-xs px-3 rounded-full" data-testid={`shared-status-select-${task.id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Pendiente">Pendiente</SelectItem>
                                    <SelectItem value="En progreso">En progreso</SelectItem>
                                    <SelectItem value="Bloqueado">Bloqueado</SelectItem>
                                    <SelectItem value="Terminado">Terminado</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              </div>
            ) : pendingInvites.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">
                    {locale === 'es-MX' ? 'No tienes tareas compartidas' : 'No shared tasks'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {locale === 'es-MX' ? 'Cuando alguien te comparta una tarea, aparecerá aquí' : 'When someone shares a task with you, it will appear here'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : loading ? (
          <div className="text-center py-12 text-muted-foreground">{t('common.loading')}</div>
        ) : filteredTasks.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              {debouncedSearch ? (
                <>
                  <Search className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground font-medium">
                    {locale === 'es-MX' ? 'No se encontraron tareas' : 'No tasks found'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {locale === 'es-MX' 
                      ? `No hay tareas que coincidan con "${debouncedSearch}"`
                      : `No tasks match "${debouncedSearch}"`
                    }
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-4 rounded-full"
                    onClick={() => setSearchQuery('')}
                  >
                    <X className="w-4 h-4 mr-2" />
                    {locale === 'es-MX' ? 'Limpiar búsqueda' : 'Clear search'}
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-muted-foreground">{t('tasks.noTasks')}</p>
                  <Button 
                    variant="outline" 
                    className="mt-4 rounded-full"
                    onClick={openNewTask}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('tasks.createFirst')}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredTasks.map((task) => {
            const StatusIcon = STATUS_CONFIG[task.status]?.icon || Clock
            const statusColor = STATUS_CONFIG[task.status]?.color || 'bg-gray-500'
            const dueDate = parseISO(task.due_date)
            const isOverdue = task.status !== 'Terminado' && isPast(dueDate)
            const urgencyConfig = URGENCY_CONFIG[task.urgencia] || URGENCY_CONFIG['MEDIO']
            const UrgencyIcon = urgencyConfig.icon
            
            return (
              <Card 
                key={task.id} 
                className={`transition-all border-l-4 ${urgencyConfig.borderColor} ${task.status === 'Vencida' ? 'border-rose-500/50' : ''} cursor-pointer hover:bg-accent/50`}
                data-testid={`task-card-${task.id}`}
                onClick={() => {
                  setSelectedTaskForDetail(task)
                  setShowDetailModal(true)
                }}
              >
                <CardContent className="p-4">
                  {/* Mobile-first layout */}
                  <div className="flex items-start gap-3">
                    {/* Status icon */}
                    <div className={`w-10 h-10 rounded-full ${statusColor} flex items-center justify-center flex-shrink-0`}>
                      <StatusIcon className="w-5 h-5 text-white" />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className={`font-semibold text-base leading-tight ${task.status === 'Terminado' ? 'line-through text-muted-foreground' : ''}`}>
                          {task.title}
                        </h3>
                        {/* Action buttons - Desktop */}
                        <div className="hidden md:flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedTaskForDetail(task)
                              setShowDetailModal(true)
                            }}
                            className="h-8 w-8"
                            title={locale === 'es-MX' ? 'Ver detalle' : 'View detail'}
                            data-testid={`view-btn-${task.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation()
                              openShareModal(task)
                            }}
                            className={`h-8 w-8 ${task.is_shared ? 'text-primary' : ''}`}
                            title={locale === 'es-MX' ? 'Compartir' : 'Share'}
                            data-testid={`share-btn-${task.id}`}
                          >
                            <Users className={`w-4 h-4 ${task.is_shared ? 'text-primary' : ''}`} />
                            {task.shared_count > 0 && (
                              <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                                {task.shared_count}
                              </span>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation()
                              openEditTask(task)
                            }}
                            className="h-8 w-8"
                            data-testid={`edit-btn-${task.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation()
                              deleteTask(task.id)
                            }}
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            data-testid={`delete-btn-${task.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {task.description && (
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                          {task.description}
                        </p>
                      )}
                      
                      {/* Date, urgency and status badges */}
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className={`text-xs flex items-center gap-1 ${isOverdue && task.status !== 'Terminado' ? 'text-rose-500' : 'text-muted-foreground'}`}>
                          <Calendar className="w-3 h-3" />
                          {format(dueDate, "d MMM, HH:mm", { locale: es })}
                        </span>
                        {/* Urgency badge */}
                        <Badge 
                          variant="outline" 
                          className={`text-xs px-2 py-0.5 flex items-center gap-1 ${urgencyConfig.textColor} border-current`}
                        >
                          <UrgencyIcon className="w-3 h-3" />
                          {urgencyConfig.label}
                        </Badge>
                        <Badge variant="outline" className={`${statusColor} text-white text-xs px-2`}>
                          {task.status}
                        </Badge>
                        {/* Sync status badge */}
                        <TaskSyncBadge syncStatus={task.sync_status} />
                        {task.is_shared && (
                          <Badge 
                            variant="outline" 
                            className="text-xs px-2 py-0.5 flex items-center gap-1 text-primary border-primary"
                            data-testid={`shared-badge-${task.id}`}
                          >
                            <Users className="w-3 h-3" />
                            {task.shared_count}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Actions - Mobile friendly */}
                      <div className="flex flex-wrap items-center gap-2">
                        {task.status === 'Vencida' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => rescheduleTask(task)}
                              className="rounded-full text-xs h-8"
                              data-testid={`reschedule-btn-${task.id}`}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Reprogramar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateTaskStatus(task.id, 'Terminado')}
                              className="rounded-full text-xs h-8"
                              data-testid={`complete-btn-${task.id}`}
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Completar
                            </Button>
                          </>
                        )}
                        
                        {/* Status selector - ALWAYS available regardless of current status */}
                        <Select
                          value={task.status}
                          onValueChange={(value) => updateTaskStatus(task.id, value)}
                        >
                          <SelectTrigger 
                            className="w-auto h-10 text-sm px-4 rounded-full border-primary/50 bg-background touch-manipulation" 
                            data-testid={`status-select-${task.id}`}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent 
                            className="z-[9999] min-w-[180px]" 
                            position="item-aligned"
                          >
                            <SelectItem value="Pendiente" className="py-3">Pendiente</SelectItem>
                            <SelectItem value="En progreso" className="py-3">En progreso</SelectItem>
                            <SelectItem value="Bloqueado" className="py-3">Bloqueado</SelectItem>
                            <SelectItem value="Terminado" className="py-3">Terminado</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {/* Mobile action buttons */}
                        <div className="flex md:hidden items-center gap-1 ml-auto">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openShareModal(task)}
                            className={`h-8 w-8 relative ${task.is_shared ? 'text-primary' : ''}`}
                            data-testid={`share-btn-mobile-${task.id}`}
                          >
                            <Users className="w-4 h-4" />
                            {task.shared_count > 0 && (
                              <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                                {task.shared_count}
                              </span>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditTask(task)}
                            className="h-8 w-8"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteTask(task.id)}
                            className="h-8 w-8 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })
        )}
      </div>

      {/* Task Bottom Sheet */}
      <BottomSheet
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setNewTaskId(null)
        }}
        title={editingTask 
          ? (locale === 'es-MX' ? 'Editar tarea' : 'Edit task')
          : (newTaskId 
            ? (locale === 'es-MX' ? 'Nueva tarea (guardada)' : 'New task (saved)')
            : (locale === 'es-MX' ? 'Nueva tarea' : 'New task')
          )
        }
        initialSnap={1}
      >
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="space-y-2">
            <Label>Título *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="¿Qué necesitas hacer?"
              className="h-12"
              data-testid="task-title-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Detalles adicionales..."
              rows={3}
              data-testid="task-description-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Fecha *</Label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                className="h-12"
                data-testid="task-date-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Hora *</Label>
              <Input
                type="time"
                value={formData.due_time}
                onChange={(e) => setFormData(prev => ({ ...prev, due_time: e.target.value }))}
                className="h-12"
                data-testid="task-time-input"
              />
            </div>
          </div>

          {/* Urgency selector */}
          <div className="space-y-2">
            <Label>Urgencia</Label>
            <div className="flex gap-2">
              {Object.entries(URGENCY_CONFIG).map(([key, config]) => {
                const Icon = config.icon
                const isSelected = formData.urgencia === key
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, urgencia: key }))}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-full border-2 transition-all text-sm font-medium ${
                      isSelected 
                        ? `${config.color} text-white border-transparent` 
                        : `border-border hover:border-current ${config.textColor}`
                    }`}
                    data-testid={`urgency-${key.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                    {config.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Evidences section - always visible */}
          <div className="border-t pt-4 mt-4">
            <TaskEvidencesLoader 
              taskId={editingTask?.id || newTaskId} 
              readOnly={false}
              onCreateTask={!editingTask && !newTaskId ? createTaskForEvidences : null}
            />
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowModal(false)}
              className="flex-1 rounded-full h-12"
            >
              {locale === 'es-MX' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button
              type="submit"
              className="flex-1 rounded-full h-12 bg-blue-500 hover:bg-blue-600"
              data-testid="task-submit-btn"
            >
              {editingTask 
                ? (locale === 'es-MX' ? 'Guardar' : 'Save')
                : (locale === 'es-MX' ? 'Crear' : 'Create')
              }
            </Button>
          </div>
        </form>
      </BottomSheet>

      {/* Share Task Modal */}
      <ShareTaskModal
        task={selectedTaskForShare}
        open={showShareModal}
        onOpenChange={setShowShareModal}
        onShareUpdate={fetchTasks}
      />

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTaskForDetail}
        open={showDetailModal}
        onOpenChange={(open) => {
          setShowDetailModal(open)
          if (!open) setSelectedTaskForDetail(null)
        }}
        onEdit={(task) => {
          setShowDetailModal(false)
          openEditTask(task)
        }}
        onStatusChange={(taskId, newStatus) => {
          // Update local state for both regular and shared tasks
          setTasks(prev => prev.map(t => 
            t.id === taskId ? { ...t, status: newStatus } : t
          ))
          setSharedTasks(prev => prev.map(t =>
            t.id === taskId ? { ...t, status: newStatus } : t
          ))
        }}
        isShared={selectedTaskForDetail?.isShared || selectedTaskForDetail?.is_shared_with_me}
      />
      </div>
    </div>
  )
}
